<template>
  <v-card class="pa-6 rounded-lg">
    <h3 class="text-center my-2">
      {{
        isEditing ? $t('Atualizar template padrão') : $t('Novo template padrão')
      }}
    </h3>

    <v-form ref="form">
      <v-row class="ma-0" dense>
        <v-col cols="12" sm="4">
          <span class="text ml-1">{{ $t('Nome do template') }}</span>
          <v-text-field
            v-model="template.name"
            class="primary-input"
            :placeholder="$t('Insira o nome do template')"
            :rules="[(v) => !!v || 'Nome obrigatório']"
            outlined
            hide-details="auto"
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="4">
          <span class="text ml-1">Departamento</span>
          <v-autocomplete
            v-model="template.departmentType"
            :items="departmentTypes"
            append-icon="mdi-chevron-down"
            item-text="text"
            item-value="value"
            class="primary-input"
            :rules="[(v) => v != null || 'Departamento obrigatório']"
            outlined
            hide-details="auto"
          >
          </v-autocomplete>
        </v-col>

        <v-col cols="12" sm="4">
          <span class="text ml-1">{{ $t('Equipamentos padrão') }}</span>
          <v-autocomplete
            multiple
            :loading="equipLoading"
            :items="standardEquipments"
            item-text="name"
            item-value="id"
            v-model="selectedEquipments"
            append-icon="mdi-chevron-down"
            class="primary-input"
            :rules="[(v) => !!v || 'Equipamento padrão obrigatório']"
            :placeholder="$t('Pesquisar por nome')"
            outlined
            clearable
            hide-details="auto"
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
            <template
              v-slot:selection="{ attrs, item, index, parent, selected }"
            >
              <v-chip
                v-if="item === Object(item) && index < 3"
                v-bind="attrs"
                color="primary"
                :input-value="selected"
                label
                small
              >
                <span class="pr-2">
                  {{ item.name }}
                </span>
                <v-icon x-small @click="parent.selectItem(item)">
                  $delete
                </v-icon>
              </v-chip>

              <span v-if="index == 3" class="grey--text text-caption">
                (+{{ template.templateStandardEquipments.length - 3 }} outros)
              </span>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12">
          <div class="d-flex justify-end">
            <v-btn
              class="secondary-button mr-2"
              depressed
              @click="show = !show"
            >
              {{ show ? 'Fechar' : 'Filtros' }}
            </v-btn>
          </div>

          <v-expand-transition>
            <div v-show="show" class="mb-6">
              <v-form ref="form">
                <v-row class="ma-0" align="center">
                  <v-col>
                    <span class="text ml-1">{{ $t('Nome') }}</span>
                    <v-text-field
                      class="primary-input"
                      :placeholder="$t('Pesquisar')"
                      v-model="filter.name"
                      outlined
                      hide-details="auto"
                    >
                    </v-text-field>
                  </v-col>

                  <v-col>
                    <span class="text ml-1">{{
                      $t('Classe de Checklist')
                    }}</span>
                    <v-autocomplete
                      :loading="classLoading"
                      :items="classes"
                      item-text="name"
                      item-value="id"
                      v-model="filter.classId"
                      append-icon="mdi-chevron-down"
                      class="primary-input"
                      :placeholder="$t('Pesquisar por nome')"
                      outlined
                      hide-details="auto"
                    >
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <div class="d-flex justify-end">
                  <v-btn class="clear-button mr-1" depressed @click="reset">
                    {{ $t('Limpar') }}
                  </v-btn>
                  <v-btn
                    @click="applyFilter"
                    class="secondary-button mr-3"
                    depressed
                  >
                    Aplicar filtros
                  </v-btn>
                </div>
              </v-form>
            </div>
          </v-expand-transition>

          <v-data-table
            :headers="headers"
            :items="items"
            :items-per-page="filter.pageSize"
            class="table-custom ma-2"
            v-model="selectedItems"
            :single-select="false"
            show-select
            :loading-text="$t('Carregando...')"
            :no-data-text="$t('Nenhum item encontrado')"
            hide-default-footer
            hide-default-header
          >
            <template #header="{ props, on }">
              <thead>
                <tr>
                  <th
                    class="text-center"
                    v-for="header in props.headers"
                    :key="header.value"
                  >
                    <v-simple-checkbox
                      v-if="header.value === 'data-table-select'"
                      v-model="props.everyItem"
                      :indeterminate="props.someItems && !props.everyItem"
                      color="purple"
                      @input="on['toggle-select-all']"
                    ></v-simple-checkbox>

                    <div>
                      <v-hover v-slot="{ hover }">
                        <div>
                          <span>{{ $t(header.text) }}</span>
                          <v-icon
                            v-if="
                              hover &&
                              header.value != 'data-table-select' &&
                              header.value != 'actions'
                            "
                            color="white"
                            class="mx-2"
                            @click="on.sort(header.value)"
                          >
                            {{
                              props.options.sortBy.includes(header.value)
                                ? `mdi-chevron-down`
                                : `mdi-chevron-up`
                            }}
                          </v-icon>
                        </div>
                      </v-hover>
                    </div>
                  </th>
                </tr>
              </thead>
            </template>

            <template #[`item.createdAt`]="{ item }">
              <span> {{ formatDate(item.createdAt) }} </span>
            </template>
          </v-data-table>

          <div class="pagination mx-2">
            <div class="item-pagination block-one">
              <div class="registros" v-if="items.length">
                <span> 1 - {{ items.length }} de {{ filter.totalItems }} </span>
              </div>
            </div>

            <div class="item-pagination block-two">
              <v-pagination
                class="pagination-item"
                v-model="filter.page"
                :length="filter.totalPages"
                :total-visible="4"
              ></v-pagination>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-form>

    <div class="d-flex justify-end mt-4">
      <v-btn class="secondary-button" depressed @click="$emit('close')">
        Cancelar
      </v-btn>
      <v-btn
        :loading="loading"
        class="primary-button"
        depressed
        @click="submit"
      >
        {{ isEditing ? $t('Atualizar') : $t('Cadastrar') }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ApiService from '../../../../../services/ApiService'
import FormatDate from '../../../../../Utils/FormatDate'

export default {
  name: 'ManageStandardChecklistTemplate',

  props: {
    checklistTemplate: Object,
  },

  async created() {
    this.institutionId = JSON.parse(localStorage.getItem('institution')).id
    this.getChecklisItemClasses()
    this.getStandardEquipments()
    await this.list()
    if (this.checklistTemplate) {
      this.template = this.checklistTemplate
      this.selectedItems = this.template.templateItems.map(
        (x) => x.standardChecklistItem
      )
      this.selectedEquipments = this.template.templateStandardEquipments.map(
        (x) => x.standardEquipmentId
      )
      this.isEditing = true
    }
  },

  data() {
    return {
      apiService: new ApiService(),
      isEditing: false,
      loading: false,
      template: {
        templateStandardEquipments: [],
        templateItems: [],
      },
      equipLoading: false,
      standardEquipments: [],
      show: false,
      filter: {
        page: 1,
        pageSize: 6,
      },
      classLoading: false,
      classes: [],
      selectedEquipments: [],
      selectedItems: [],
      items: [],
      departmentTypes: [
        { text: 'Engenharia clínica', value: 0 },
        { text: 'Manutenção predial', value: 1 },
      ],
      headers: [
        {
          text: 'Item',

          align: 'center',
          value: 'name',
        },
        {
          text: 'Classe',

          align: 'center',
          value: 'checklistItemClass.name',
        },
        {
          text: 'Data de criação',

          align: 'center',
          value: 'createdAt',
        },
      ],
    }
  },

  watch: {
    'filter.pageSize': {
      deep: true,
      handler() {
        this.list()
      },
    },
    'filter.page': {
      deep: true,
      handler() {
        this.list()
      },
    },
  },

  methods: {
    formatDate(date) {
      return FormatDate.dateTime(date)
    },
    applyFilter() {
      this.list()
    },

    reset() {
      this.filter.page = 1
      this.filter.name = ''
      this.filter.classId = ''
      this.list()
    },

    async list() {
      this.filter.templateId = this.checklistTemplate
        ? this.checklistTemplate.id
        : ''

      await this.apiService
        .post('standardChecklistItem/list/paginate', this.filter)
        .then((response) => {
          this.items = response.content.entities

          const pager = response.content.pager
          this.filter.page = pager.currentPage
          this.filter.pageSize = pager.pageSize
          this.filter.totalItems = pager.totalItems
          this.filter.totalPages = pager.totalPages
        })
    },

    async getChecklisItemClasses() {
      this.classLoading = true

      await this.apiService.get(`checklistItemClass/list`).then((response) => {
        this.classes = response.content
        this.classLoading = false
      })
    },

    getStandardEquipments() {
      this.equipLoading = true
      this.apiService
        .get('standardEquipment/list')
        .then((response) => {
          this.standardEquipments = response.content
          this.equipLoading = false
        })
        .catch(() => {
          this.equipLoading = false
        })
    },

    submit() {
      if (this.$refs.form.validate()) {
        this.loading = true

        this.template.templateItems = this.selectedItems.map((x) => {
          return {
            standardChecklistItemId: x.id,
          }
        })

        this.template.templateStandardEquipments = this.selectedEquipments.map(
          (x) => {
            return {
              standardEquipmentId: x,
            }
          }
        )

        if (this.isEditing) {
          this.apiService
            .put('standardChecklistTemplate/edit', this.template)
            .then(() => {
              this.$emit('confirm')
              this.loading = false
            })
            .catch(() => {
              this.loading = false
            })
        } else {
          this.apiService
            .post('standardChecklistTemplate/add', this.template)
            .then(() => {
              this.$emit('confirm')
              this.loading = false
            })
            .catch(() => {
              this.loading = false
            })
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.table-custom {
  &::v-deep .v-data-table__wrapper {
    thead {
      border: none !important;

      th {
        border: none !important;
        &:first-child {
          border-radius: 4px 0 0 4px;
        }

        &:last-child {
          border-radius: 0 4px 4px 0;
        }
      }
    }
  }
}
.pagination {
  margin: 1.5rem 0;
  background: $grey-1;
  border-radius: 4px;
  padding: 0.2rem 1.3rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .item-pagination {
    flex: 1 1 100px;
    margin: 0.5rem 0;
    .btn {
      border-radius: 4px;
      background: #c7c9ca;
      font-weight: bold;
    }
    .registros {
      span {
        font-weight: 700;
        font-size: 12px;
        line-height: 16px;
        color: $grey-2;
      }
    }
  }
  .block-one {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .block-two {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .pagination-item {
      &::v-deep .v-pagination {
        li {
          button {
            box-shadow: none !important;
            background: none !important;
            color: $grey-2;
            font-weight: 500;
          }

          .v-pagination__item--active {
            color: $black-1;
          }
        }
      }
    }
  }

  @media (max-width: 406px) {
    .block-one {
      justify-content: center;
    }
    .block-two {
      justify-content: center;
    }
  }
}
</style>
